import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { proxyMCPRequest } from '../api/mcpProxy'
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch'
import { usePageVisible } from '../hooks/usePageVisible'
import type {
  NexthinkActionStatus,
  NexthinkActionTracker,
  NexthinkTrackedAction,
  RegisterNexthinkActionInput,
} from '../types/nexthinkTracker'

const STORAGE_KEY = 'nexthinkActiveActions'
const POLL_INTERVAL_MS = 7000
const TTL_MS = 30 * 60 * 1000

const NexthinkActionTrackerContext =
  createContext<NexthinkActionTracker | null>(null)

function loadPersisted(): NexthinkTrackedAction[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as NexthinkTrackedAction[]) : []
  } catch {
    return []
  }
}

// deviceUid identifies a specific managed device — keep it in memory only
// (registerTriggeredAction / actionsRef) so it's available for the current
// tab's polling, but never write it to localStorage.
function persist(actions: NexthinkTrackedAction[]): void {
  try {
    const sanitized = actions.map(({ deviceUid: _deviceUid, ...rest }) => rest)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized))
  } catch {
    // storage unavailable — tracking still works for this tab session
  }
}

/**
 * Status results are raw NQL output with an action-dependent shape, so — like
 * the widget's own poller — scan the payload for a terminal status.
 */
function readOutcome(data: unknown): 'success' | 'failure' | 'pending' {
  const serialized = JSON.stringify(data ?? '').toLowerCase()
  if (serialized.includes('"success"') || serialized.includes('completed')) {
    return 'success'
  }
  if (serialized.includes('"failure"') || serialized.includes('failed')) {
    return 'failure'
  }
  return 'pending'
}

export function NexthinkActionTrackerProvider({
  children,
}: {
  children: ReactNode
}) {
  const authenticatedFetch = useAuthenticatedFetch()
  const authenticatedFetchRef = useRef(authenticatedFetch)
  const [actions, setActions] = useState<NexthinkTrackedAction[]>(() =>
    loadPersisted().filter((action) => Date.now() - action.triggeredAt < TTL_MS),
  )
  const actionsRef = useRef(actions)
  const [pollingActive, setPollingActive] = useState(false)
  const pageVisible = usePageVisible()

  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch
  })

  useEffect(() => {
    actionsRef.current = actions
    persist(actions)
  }, [actions])

  const registerTriggeredAction = useCallback(
    (input: RegisterNexthinkActionInput) => {
      setActions((current) => {
        if (current.some((action) => action.requestId === input.requestId)) {
          return current
        }
        const next: NexthinkTrackedAction = {
          ...input,
          status: 'pending',
          triggeredAt: Date.now(),
        }
        return [...current, next]
      })
    },
    [],
  )

  // Polls only while the activities panel is open and the tab is visible. Each
  // round starts after the previous one finishes, so slow requests never overlap.
  const shouldPoll = pollingActive && pageVisible
  useEffect(() => {
    if (!shouldPoll) return
    let cancelled = false
    let timer: number | undefined

    const pollOnce = async () => {
      const now = Date.now()
      const pending = actionsRef.current.filter(
        (action) => action.status === 'pending' && now - action.triggeredAt < TTL_MS,
      )

      if (pending.length === 0) {
        setActions((current) => {
          const live = current.filter((action) => now - action.triggeredAt < TTL_MS)
          return live.length === current.length ? current : live
        })
        return
      }

      const updates = await Promise.all(
        pending.map(async (action) => {
          try {
            const result = await proxyMCPRequest<{
              structuredContent?: unknown
            }>(authenticatedFetchRef.current, action.proxyEndpoint, {
              serverId: action.serverId,
              serverHash: action.serverHash,
              method: 'tools/call',
              params: {
                name: 'nexthink-action-status',
                arguments: { requestId: action.requestId },
              },
            })
            const outcome = readOutcome(result?.structuredContent)
            return outcome === 'pending'
              ? null
              : { requestId: action.requestId, status: outcome }
          } catch {
            return null
          }
        }),
      )
      if (cancelled) return

      const resolved = new Map<string, Exclude<NexthinkActionStatus, 'pending'>>()
      for (const update of updates) if (update) resolved.set(update.requestId, update.status)

      setActions((current) =>
        current
          .filter((action) => Date.now() - action.triggeredAt < TTL_MS)
          .map((action) => {
            const status = resolved.get(action.requestId)
            return status && action.status === 'pending' ? { ...action, status } : action
          }),
      )
    }

    const loop = async () => {
      await pollOnce()
      if (!cancelled) timer = window.setTimeout(loop, POLL_INTERVAL_MS)
    }
    void loop()

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [shouldPoll])

  const value = useMemo(
    () => ({ actions, registerTriggeredAction, setPollingActive }),
    [actions, registerTriggeredAction],
  )

  return (
    <NexthinkActionTrackerContext.Provider value={value}>
      {children}
    </NexthinkActionTrackerContext.Provider>
  )
}

export function useNexthinkActionTracker(): NexthinkActionTracker {
  const context = useContext(NexthinkActionTrackerContext)
  if (!context) {
    throw new Error(
      'useNexthinkActionTracker must be used within a NexthinkActionTrackerProvider',
    )
  }
  return context
}
